import React, { useEffect, useMemo, useCallback, useReducer } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase, Database } from '../lib/supabase'
import BulkUpload from '../components/BulkUpload'
import {
  MagnifyingGlassIcon,
  AdjustmentsHorizontalIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  PhotoIcon,
  CloudArrowUpIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline'

type CookieCutter = Database['public']['Tables']['cookie_cutters']['Row'] & {
  storage_bins?: Database['public']['Tables']['storage_bins']['Row'] | null
}

type StorageBin = { id: string; name: string }

type SortField = 'name' | 'category' | 'created_at' | 'storage_bin'
type SortDirection = 'asc' | 'desc'

const PAGE_SIZE = 25

// State shape consolidated into a single object
interface InventoryState {
  cookieCutters: CookieCutter[]
  storageBins: StorageBin[]
  totalCount: number
  loading: boolean
  error: string | null
  searchQuery: string
  selectedCategory: string
  selectedBin: string
  sortField: SortField
  sortDirection: SortDirection
  currentPage: number
  showBulkUpload: boolean
}

type InventoryAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_DATA'; payload: { cookieCutters: CookieCutter[]; storageBins: StorageBin[]; totalCount: number } }
  | { type: 'SET_ERROR'; payload: string }
  | { type: 'SET_SEARCH'; payload: string }
  | { type: 'SET_CATEGORY'; payload: string }
  | { type: 'SET_BIN'; payload: string }
  | { type: 'SET_SORT'; payload: { field: SortField; direction: SortDirection } }
  | { type: 'SET_PAGE'; payload: number }
  | { type: 'TOGGLE_BULK_UPLOAD' }
  | { type: 'CLEAR_FILTERS' }

const initialState: InventoryState = {
  cookieCutters: [],
  storageBins: [],
  totalCount: 0,
  loading: true,
  error: null,
  searchQuery: '',
  selectedCategory: '',
  selectedBin: '',
  sortField: 'created_at',
  sortDirection: 'desc',
  currentPage: 0,
  showBulkUpload: false,
}

function inventoryReducer(state: InventoryState, action: InventoryAction): InventoryState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload }
    case 'SET_DATA':
      return {
        ...state,
        cookieCutters: action.payload.cookieCutters,
        storageBins: action.payload.storageBins,
        totalCount: action.payload.totalCount,
        loading: false,
        error: null,
      }
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false }
    case 'SET_SEARCH':
      return { ...state, searchQuery: action.payload, currentPage: 0 }
    case 'SET_CATEGORY':
      return { ...state, selectedCategory: action.payload, currentPage: 0 }
    case 'SET_BIN':
      return { ...state, selectedBin: action.payload, currentPage: 0 }
    case 'SET_SORT':
      return { ...state, sortField: action.payload.field, sortDirection: action.payload.direction, currentPage: 0 }
    case 'SET_PAGE':
      return { ...state, currentPage: action.payload }
    case 'TOGGLE_BULK_UPLOAD':
      return { ...state, showBulkUpload: !state.showBulkUpload }
    case 'CLEAR_FILTERS':
      return { ...state, searchQuery: '', selectedCategory: '', selectedBin: '', currentPage: 0 }
    default:
      return state
  }
}

export default function Inventory() {
  const { user, loading: authLoading } = useAuth()
  const [state, dispatch] = useReducer(inventoryReducer, initialState)

  const {
    cookieCutters,
    storageBins,
    totalCount,
    loading,
    error,
    searchQuery,
    selectedCategory,
    selectedBin,
    sortField,
    sortDirection,
    currentPage,
    showBulkUpload,
  } = state

  // Debounced search value for server-side filtering
  const [debouncedSearch, setDebouncedSearch] = React.useState('')

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  const fetchData = useCallback(async () => {
    if (!user) {
      dispatch({ type: 'SET_LOADING', payload: false })
      return
    }

    dispatch({ type: 'SET_LOADING', payload: true })
    try {
      // Build query with server-side filtering, sorting, and pagination
      let query = supabase
        .from('cookie_cutters')
        .select(`
          *,
          storage_bins (
            id,
            name
          )
        `, { count: 'exact' })
        .eq('user_id', user.id)

      // Server-side search filtering using ilike
      if (debouncedSearch.trim()) {
        query = query.or(`name.ilike.%${debouncedSearch}%,description.ilike.%${debouncedSearch}%,category.ilike.%${debouncedSearch}%`)
      }

      // Server-side category filtering
      if (selectedCategory) {
        query = query.eq('category', selectedCategory)
      }

      // Server-side bin filtering
      if (selectedBin === 'unassigned') {
        query = query.is('bin_id', null)
      } else if (selectedBin) {
        query = query.eq('bin_id', selectedBin)
      }

      // Server-side sorting
      const sortColumn = sortField === 'storage_bin' ? 'bin_id' : sortField
      query = query.order(sortColumn, { ascending: sortDirection === 'asc' })

      // Pagination
      const from = currentPage * PAGE_SIZE
      const to = from + PAGE_SIZE - 1
      query = query.range(from, to)

      const { data: cuttersData, error: cuttersError, count } = await query

      if (cuttersError) {
        dispatch({ type: 'SET_DATA', payload: { cookieCutters: [], storageBins: [], totalCount: 0 } })
        return
      }

      // Extract unique storage bins from the cookie cutters data (eliminates N+1 query)
      // We still need all bins for the filter dropdown, so fetch them once
      let binsData = storageBins
      if (storageBins.length === 0) {
        const { data: allBins } = await supabase
          .from('storage_bins')
          .select('id, name')
          .eq('user_id', user.id)
          .order('name')
        binsData = allBins || []
      }

      dispatch({
        type: 'SET_DATA',
        payload: {
          cookieCutters: cuttersData || [],
          storageBins: binsData,
          totalCount: count || 0,
        },
      })
    } catch {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to load inventory. Please try again.' })
    }
  }, [user, debouncedSearch, selectedCategory, selectedBin, sortField, sortDirection, currentPage, storageBins])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Get unique categories from current page data (for display) + all known categories
  const categories = useMemo(() => {
    const uniqueCategories = new Set(cookieCutters.map(cc => cc.category).filter(Boolean))
    return Array.from(uniqueCategories) as string[]
  }, [cookieCutters])

  const handleSort = useCallback((field: SortField) => {
    if (sortField === field) {
      dispatch({ type: 'SET_SORT', payload: { field, direction: sortDirection === 'asc' ? 'desc' : 'asc' } })
    } else {
      dispatch({ type: 'SET_SORT', payload: { field, direction: 'asc' } })
    }
  }, [sortField, sortDirection])

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return <AdjustmentsHorizontalIcon className="h-4 w-4 text-gray-400" />
    }
    return sortDirection === 'asc' ? (
      <ChevronUpIcon className="h-4 w-4 text-blue-500" />
    ) : (
      <ChevronDownIcon className="h-4 w-4 text-blue-500" />
    )
  }

  const totalPages = Math.ceil(totalCount / PAGE_SIZE)
  const canGoPrevious = currentPage > 0
  const canGoNext = currentPage < totalPages - 1

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Inventory</h1>
            <p className="mt-1 text-sm text-gray-700">
              Search, filter, and manage your cookie cutter collection
            </p>
          </div>
          <button
            onClick={() => dispatch({ type: 'TOGGLE_BULK_UPLOAD' })}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <CloudArrowUpIcon className="h-4 w-4 mr-2" />
            {showBulkUpload ? 'Hide' : 'Bulk Upload'}
          </button>
        </div>
      </div>

      {/* Bulk Upload Component */}
      {showBulkUpload && (
        <div className="mb-6">
          <BulkUpload
            onComplete={() => {
              fetchData()
              dispatch({ type: 'TOGGLE_BULK_UPLOAD' })
            }}
          />
        </div>
      )}

      {/* Filters */}
      <div className="bg-white shadow rounded-lg mb-6">
        <div className="px-4 py-5 sm:p-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {/* Search */}
            <div>
              <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-2">
                Search
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  id="search"
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Search cookie cutters..."
                  value={searchQuery}
                  onChange={(e) => dispatch({ type: 'SET_SEARCH', payload: e.target.value })}
                />
              </div>
            </div>

            {/* Category Filter */}
            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-2">
                Category
              </label>
              <select
                id="category"
                className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={selectedCategory}
                onChange={(e) => dispatch({ type: 'SET_CATEGORY', payload: e.target.value })}
              >
                <option value="">All categories</option>
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>

            {/* Storage Bin Filter */}
            <div>
              <label htmlFor="storage-bin" className="block text-sm font-medium text-gray-700 mb-2">
                Storage Bin
              </label>
              <select
                id="storage-bin"
                className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={selectedBin}
                onChange={(e) => dispatch({ type: 'SET_BIN', payload: e.target.value })}
              >
                <option value="">All storage bins</option>
                <option value="unassigned">Unassigned</option>
                {storageBins.map((bin) => (
                  <option key={bin.id} value={bin.id}>
                    {bin.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Clear Filters */}
            <div className="flex items-end">
              <button
                onClick={() => dispatch({ type: 'CLEAR_FILTERS' })}
                className="w-full bg-gray-100 border border-gray-300 rounded-md py-2 px-4 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Clear Filters
              </button>
            </div>
          </div>

          {/* Results Count */}
          <div className="mt-4 text-sm text-gray-700">
            Showing {cookieCutters.length} of {totalCount} cookie cutters
            {totalPages > 1 && ` (page ${currentPage + 1} of ${totalPages})`}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Photo
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Name</span>
                    <SortIcon field="name" />
                  </div>
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('category')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Category</span>
                    <SortIcon field="category" />
                  </div>
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('storage_bin')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Storage Bin</span>
                    <SortIcon field="storage_bin" />
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('created_at')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Added</span>
                    <SortIcon field="created_at" />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(authLoading || loading) ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-gray-700">
                    Loading...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-red-600">
                    {error}
                    <button
                      onClick={() => dispatch({ type: 'SET_LOADING', payload: true })}
                      className="ml-2 text-blue-600 hover:text-blue-800 underline"
                    >
                      Retry
                    </button>
                  </td>
                </tr>
              ) : cookieCutters.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-gray-700">
                    No cookie cutters found
                  </td>
                </tr>
              ) : (
                cookieCutters.map((cutter) => (
                  <tr key={cutter.id} className="hover:bg-gray-50 cursor-pointer focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-inset">
                    <Link to={`/inventory/${cutter.id}`} className="contents focus:outline-none">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="h-12 w-12 flex-shrink-0">
                          {cutter.photo_url ? (
                            <img
                              className="h-12 w-12 rounded-md object-cover"
                              src={cutter.photo_url}
                              alt={cutter.name}
                              loading="lazy"
                            />
                          ) : (
                            <div className="h-12 w-12 rounded-md bg-gray-200 flex items-center justify-center">
                              <PhotoIcon className="h-6 w-6 text-gray-400" />
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 hover:text-blue-600">{cutter.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {cutter.category ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {cutter.category}
                          </span>
                        ) : (
                          <span className="text-gray-600 text-sm">Uncategorized</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {cutter.storage_bins ? (
                          <span className="text-sm text-gray-900">{cutter.storage_bins.name}</span>
                        ) : (
                          <span className="text-gray-600 text-sm">Unassigned</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 max-w-xs truncate">
                          {cutter.description || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {new Date(cutter.created_at).toLocaleDateString()}
                      </td>
                    </Link>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => dispatch({ type: 'SET_PAGE', payload: currentPage - 1 })}
                disabled={!canGoPrevious}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => dispatch({ type: 'SET_PAGE', payload: currentPage + 1 })}
                disabled={!canGoNext}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{currentPage * PAGE_SIZE + 1}</span> to{' '}
                  <span className="font-medium">{Math.min((currentPage + 1) * PAGE_SIZE, totalCount)}</span> of{' '}
                  <span className="font-medium">{totalCount}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => dispatch({ type: 'SET_PAGE', payload: currentPage - 1 })}
                    disabled={!canGoPrevious}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Previous</span>
                    <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                  <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                    Page {currentPage + 1} of {totalPages}
                  </span>
                  <button
                    onClick={() => dispatch({ type: 'SET_PAGE', payload: currentPage + 1 })}
                    disabled={!canGoNext}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Next</span>
                    <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
